import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { api } from '../api';

// Define types
export interface Question {
  id: string;
  text: string;
  type: 'multiple_choice';
  options: string[];
  required: boolean;
  order: number;
}

export interface Presentation {
  id: string;
  title: string;
  description: string;
  date: string;
  presenter: string;
  status: 'draft' | 'active' | 'completed';
  questions: Question[];
  createdAt: string;
  updatedAt: string;
}

interface PresentationState {
  activePresentation: Presentation | null;
  currentQuestionIndex: number;
  isVotingOpen: boolean;
  error: string | null;
}

// Initial state
const initialState: PresentationState = {
  activePresentation: null,
  currentQuestionIndex: 0,
  isVotingOpen: false,
  error: null,
};

// Create slice
const presentationSlice = createSlice({
  name: 'presentations',
  initialState,
  reducers: {
    setActivePresentation: (state, action: PayloadAction<Presentation>) => {
      state.activePresentation = action.payload;
      state.currentQuestionIndex = 0;
      state.isVotingOpen = false;
    },
    clearActivePresentation: (state) => {
      state.activePresentation = null;
      state.currentQuestionIndex = 0;
      state.isVotingOpen = false;
    },
    nextQuestion: (state) => {
      if (state.activePresentation && 
          state.currentQuestionIndex < state.activePresentation.questions.length - 1) {
        state.currentQuestionIndex += 1;
      }
    },
    previousQuestion: (state) => {
      if (state.currentQuestionIndex > 0) {
        state.currentQuestionIndex -= 1;
      }
    },
    goToQuestion: (state, action: PayloadAction<number>) => {
      if (state.activePresentation && 
          action.payload >= 0 && 
          action.payload < state.activePresentation.questions.length) {
        state.currentQuestionIndex = action.payload;
      }
    },
    openVoting: (state) => {
      state.isVotingOpen = true;
    },
    closeVoting: (state) => {
      state.isVotingOpen = false;
    },
    setError: (state, action: PayloadAction<string>) => {
      state.error = action.payload;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    // Handle API responses
    builder.addMatcher(
      api.endpoints.getPresentationById.matchFulfilled,
      (state, { payload }) => {
        // If this is the active presentation, update it
        if (state.activePresentation && state.activePresentation.id === payload.id) {
          state.activePresentation = payload;
        }
      }
    );
  },
});

export const {
  setActivePresentation,
  clearActivePresentation,
  nextQuestion,
  previousQuestion,
  goToQuestion,
  openVoting,
  closeVoting,
  setError,
  clearError,
} = presentationSlice.actions;

export default presentationSlice.reducer;
